import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Field } from 'redux-form';
import { components } from '@glowforge/ui-common';
import { formatPrice } from 'app/util';
import AffirmAsLowAsDisplay from 'app/common/components/affirm/affirm-as-low-as-display';
import features from 'app/common/containers/features';

import './product-selector.less';

const { Markdown, Radio, Checkbox } = components;


const withAutoCompleteOff = Component => props => (
  <Component{...props} input={{ ...props.input, autoComplete: 'off' }} />
);

const RadioWithAutoCompleteOff = withAutoCompleteOff(Radio);
const CheckboxWithAutoCompleteOff = withAutoCompleteOff(Checkbox);

const ProductSelector = (props) => {
  const idString = props.id.toString();
  const soldOut = (props.backordered && props.sellOutable);
  const selected = props.selectedProduct === idString;

  let badge;
  if (props.badge || props.badgeHidden) {
    badge = (
      <div className={`popularBadge ${props.badgeHidden ? 'inactive' : ''}`}>
        {props.badge || props.badgeHidden}
      </div>
    );
  }

  let field;
  if (props.type === 'radio') {
    field = (
      <Field
        autocomplete={false}
        type='radio'
        name='productPicker'
        id={`product_${props.id}`}
        value={idString}
        disabled={soldOut}
        component={RadioWithAutoCompleteOff} />
    );
  } else {
    field = (
      <Field
        autocomplete={false}
        type='checkbox'
        id={`product_${props.id}`}
        name={props.name}
        value={idString}
        component={CheckboxWithAutoCompleteOff} />
    );
  }

  let total = props.price;
  let price = [];
  // Base Price is controlled by contentful as just a basic text string
  if (props.basePrice) {
    price = [
      <div className='origPrice base-price-strikethrough' key='basePrice'>{props.basePrice}</div>,
    ];
  }

  if (props.showReferralInfo) {
    total = props.price + props.referral.discount;
    price.push(
      <div className='origPrice' key='origPrice'>{formatPrice(props.price)}</div>,
    );
    price.push(
      <div className='finalPrice' key='finalPriceReferral'>
        <span className='withReferral'> with referral</span>
        { formatPrice(total) }
      </div>,
    );
  } else {
    price.push(<div className='finalPrice' key='finalPrice'>{formatPrice(props.price)}</div>);
  }

  const affirm = props.includeAffirm && !props.backordered
    ? (<AffirmAsLowAsDisplay amount={total} hideLearnMore />)
    : null;

  const greenText = props.greenText && (
    <Markdown
      source={props.greenText}
      className='limited' />
  );

  // delivery comes from Backend and is specifically for US delivery dates.
  // app/views/storefront/api/v1/products/glowforge/_product.json.jbuilder-9
  // So, only show it for US
  const shipTime = props.isIpAddressInUS ? props.delivery : '';

  /* eslint-disable jsx-a11y/label-has-for */
  return (
    <label
      className={classnames('ProductSelector', props.className, {
        selectedProduct: selected,
        soldOut,
      })}>
      {badge}
      <div className='selector'>
        {field}
        <div className='info'>
          <h4 className='h3 title'>
            {props.displayName || props.name}
            {greenText}
            <Markdown source={soldOut ? props.soldOutMessage : shipTime} className='shipTime' />
          </h4>
          <div className='productPrice'>
            {price}
            {affirm}
          </div>
          <Markdown source={props.description} className='description' />
        </div>
      </div>
    </label>
  );
  /* eslint-enable jsx-a11y/label-has-for */
};

export const productPropType = {
  description: PropTypes.string,
  subTitle: PropTypes.string,
  id: PropTypes.number,
  msrp: PropTypes.number,
  price: PropTypes.number,
  name: PropTypes.string,
  sku: PropTypes.string,
  formattedDeliveryDate: PropTypes.string,
};

ProductSelector.propTypes = {
  ...productPropType,
  badge: PropTypes.string,
  // badgeHidden implemented for the sake of A/B testing (add to page but using display:none)
  badgeHidden: PropTypes.string,
  type: PropTypes.oneOf(['checkbox', 'radio']),
  displayName: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  selectedProduct: PropTypes.string,
  greenText: PropTypes.string,
  includeAffirm: PropTypes.bool,
  className: PropTypes.string,
};

ProductSelector.defaultProps = {
  type: 'radio',
  badge: null,
  badgeHidden: null,
  className: '',
  displayName: null,
  selectedProduct: null,
  greenText: '',
  includeAffirm: false,
};

export default features(ProductSelector);



// WEBPACK FOOTER //
// ./app/store/components/glowforge/product-selector.jsx